import { useGetAiWorkspace, useGetAiWorkspaceRequests, useUpdateAiWorkspace } from "@entities/aiWorkspace";
import { PROJECT_AI_MODULES } from "@pages/AIWorkspace/modules";
import { format } from "date-fns";
import { ArrowLeft, FolderOpen, LayoutGrid, Loader2, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CreatorFitTab } from "./CreatorFitTab";
import { DraftVetTab } from "./DraftVetTab";
import { OverviewTab } from "./OverviewTab";
import { ScriptGenerationPane } from "./ScriptGenerationPane";
import { ScriptVetTab } from "./ScriptVetTab";
import { hasSavedText, moduleHasSavedPrompt, requestIsWorking } from "./sidebarStatus";
import styles from "./styles.module.scss";
import { useAiWorkspaceModuleParams } from "./useAiWorkspaceModuleParams";
import { WorkspaceOverviewHeader } from "./WorkspaceOverviewHeader";

export const AIWorkspaceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isFetching } = useGetAiWorkspace(id || "");
  const { data: requestsData } = useGetAiWorkspaceRequests(id || "");
  const { update, isUpdating } = useUpdateAiWorkspace({
    onSuccess: () => {
      setEditing(false);
    },
    showSuccessToast: true,
  });
  const workspace = data?.data;
  const requests = requestsData?.data || [];

  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [navOpen, setNavOpen] = useState(true);
  const {
    activeTab,
    requestId,
    creatorScriptGeneratorJobId,
    isOpeningRequest,
    setTab,
    openRequest,
    closeRequest,
    selectCreatorScriptJob,
  } = useAiWorkspaceModuleParams();

  useEffect(() => {
    if (workspace?.name) setNameDraft(workspace.name);
  }, [workspace?.name]);

  const commitRename = () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === workspace?.name) {
      setNameDraft(workspace?.name || "");
      setEditing(false);
      return;
    }
    update({ aiWorkspaceId: id!, name: trimmed });
  };

  if (!workspace && isFetching) {
    return <div style={{ padding: "40px", textAlign: "center" }}>Loading project...</div>;
  }

  if (!workspace) return null;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.breadcrumbs}>
        <div className={styles.breadcrumbLeft}>
          <button onClick={() => navigate("/ai-workspace")} className={styles.breadcrumbLink}>
            <ArrowLeft size={14} /> AI Workspace
          </button>
          <span className={styles.breadcrumbSeparator}>/</span>
          <div className={styles.breadcrumbCurrent}>
            <FolderOpen size={14} className={styles.folderIcon} />
            <h1 className={styles.breadcrumbTitle}>{workspace.name}</h1>
          </div>
        </div>
        <button onClick={() => setNavOpen(!navOpen)} className={styles.navToggleBtn}>
          {navOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
          <span>{navOpen ? "Hide modules" : "Modules"}</span>
        </button>
      </div>

      <div className={styles.workspaceLayout}>
        <aside className={`${styles.sidebar} ${navOpen ? styles.sidebarOpen : styles.sidebarClosed}`}>
          <div className={styles.sidebarContent}>
            <div className={styles.sidebarSection}>
              <div className={styles.sidebarSectionTitle}>In this project</div>
              <button 
                className={`${styles.navItem} ${activeTab === "overview" ? styles.navItemActive : ""}`} 
                onClick={() => setTab("overview")}
              >
                <LayoutGrid size={15} className={styles.navIcon} />
                <span className={styles.navText}>Overview</span>
                {hasSavedText(workspace.overview) ? (
                  <span className={styles.savedDotOverview} title="Overview saved" />
                ) : null}
              </button>
            </div>

            <div className={styles.sidebarSection}>
              <div className={styles.sidebarSectionTitle}>Check modules</div>
              <div className={styles.sidebarSectionSubtitle}>One master prompt per module</div>
              <div className={styles.navModules}>
                {PROJECT_AI_MODULES.map((mod) => {
                  const Icon = mod.icon;
                  const configured = moduleHasSavedPrompt(workspace, mod.id);
                  const hasWorking = requests.some((request) => requestIsWorking(request, mod.id));

                  return (
                    <button 
                      key={mod.id} 
                      className={`${styles.navItem} ${activeTab === mod.id ? styles.navItemActive : ""}`} 
                      onClick={() => setTab(mod.id)}
                    >
                      <Icon size={15} className={styles.navIcon} />
                      <span className={styles.navText}>{mod.title}</span>
                      {configured && <span className={styles.savedDotModule} title="Master prompt saved" />}
                      {hasWorking && <Loader2 size={12} className={`spin-animation ${styles.workingIcon}`} />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>

        <main className={styles.mainContent}>
          {isOpeningRequest ? (
            <p className={styles.metaText}>Loading check…</p>
          ) : null}

          {!isOpeningRequest && activeTab === "overview" && (
            <div className="animate-in fade-in duration-200">
              <WorkspaceOverviewHeader
                name={workspace.name}
                updatedLabel={
                  workspace.updatedAt
                    ? format(new Date(workspace.updatedAt), "MMM d, yyyy")
                    : "-"
                }
                editing={editing}
                nameDraft={nameDraft}
                isUpdating={isUpdating}
                onNameDraft={setNameDraft}
                onStartEdit={() => setEditing(true)}
                onCancelEdit={() => {
                  setNameDraft(workspace.name);
                  setEditing(false);
                }}
                onCommit={commitRename}
              />
              <OverviewTab
                workspace={workspace}
                requests={requests}
                onOpenRequest={openRequest}
              />
            </div>
          )}

          {!isOpeningRequest && activeTab === "creator-fit" && (
            <div className="animate-in fade-in duration-200">
              <header className={styles.header}>
                <p className={styles.headerSubtitle}>Creator Fit</p>
              </header>
              <CreatorFitTab 
                key={requestId || "creator-fit"}
                workspace={workspace} 
                requestId={requestId}
                onOpenRequest={openRequest}
                onCloseRequest={closeRequest}
              />
            </div>
          )}

          <ScriptGenerationPane
            active={!isOpeningRequest && activeTab === "script-generation"}
            workspace={workspace}
            requestId={requestId}
            jobId={creatorScriptGeneratorJobId}
            onOpenRequest={openRequest}
            onSelectCreatorJob={selectCreatorScriptJob}
          />

          {!isOpeningRequest && activeTab === "script-vet" && (
            <ScriptVetTab
              workspace={workspace}
              requests={requests}
              requestId={requestId}
              onOpenRequest={openRequest}
              onCloseRequest={closeRequest}
            />
          )}

          {!isOpeningRequest && activeTab === "draft-vet" && (
            <DraftVetTab
              workspace={workspace}
              requests={requests}
              requestId={requestId}
              onOpenRequest={openRequest}
              onCloseRequest={closeRequest}
            />
          )}
        </main>
      </div>
    </div>
  );
};
